import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

const YC_MOBILE_BREAKPOINT = '(max-width: 749px)';
const YC_SAVE_PILL_CLASS = 'yc-mobile-save-pill';
const YC_PRICE_ROW_CLASS = 'yc-mobile-price-row';
const YC_SEEDS_CLASS = 'yc-mobile-seeds-inline';
const YC_STYLE_ID = 'yc-mobile-main-price-row-inline';

class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.enhanceMobileMainPriceRow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    const newProductPrice = event.detail.data.html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
    if (!newProductPrice) return;

    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      newPrice?.insertAdjacentElement('afterend', newNote.cloneNode(true));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    this.enhanceMobileMainPriceRow();
  };

  enhanceMobileMainPriceRow() {
    if (!window.matchMedia(YC_MOBILE_BREAKPOINT).matches) return;
    if (!this.closest('.product-details')) return;
    if (!document.querySelector("main[data-template*='product']")) return;

    this.ensureMobilePriceStyles();

    const priceContainer = this.querySelector("[ref='priceContainer']");
    if (!priceContainer) return;

    const groups = Array.from(priceContainer.querySelectorAll(":scope > span[role='group']"));
    const saleGroup = groups.find((node) => node.querySelector('.price'));
    const compareGroup = groups.find((node) => node.querySelector('.compare-at-price'));
    if (!saleGroup) return;

    priceContainer.classList.add(YC_PRICE_ROW_CLASS);
    saleGroup.classList.add('yc-sale-group');
    compareGroup?.classList.add('yc-compare-group');

    let savePill = priceContainer.querySelector(`:scope > .${YC_SAVE_PILL_CLASS}`);
    if (!savePill) {
      savePill = document.createElement('span');
      savePill.className = YC_SAVE_PILL_CLASS;
      savePill.hidden = true;
      priceContainer.appendChild(savePill);
    }

    const detailsScope = this.closest('.product-details');
    const loyaltyWrapper =
      detailsScope?.querySelector('.bw-loyalty-wrapper.is-product') || detailsScope?.querySelector('.bw-loyalty-wrapper');
    if (loyaltyWrapper) {
      loyaltyWrapper.classList.add(YC_SEEDS_CLASS);
      if (loyaltyWrapper.parentElement !== priceContainer) {
        priceContainer.appendChild(loyaltyWrapper);
      }
    }

    this.updateSavePill(priceContainer, savePill);
  }

  updateSavePill(priceContainer, savePill) {
    const saleText = priceContainer.querySelector('.yc-sale-group .price')?.textContent || '';
    const compareText = priceContainer.querySelector('.yc-compare-group .compare-at-price')?.textContent || '';
    const saleValue = this.parseMoney(saleText);
    const compareValue = this.parseMoney(compareText);

    const canShow = Number.isFinite(saleValue) && Number.isFinite(compareValue) && compareValue > saleValue && compareValue > 0;
    if (!canShow) {
      savePill.hidden = true;
      savePill.textContent = '';
      return;
    }

    const savePercent = Math.round(((compareValue - saleValue) / compareValue) * 100);
    if (!Number.isFinite(savePercent) || savePercent <= 0) {
      savePill.hidden = true;
      savePill.textContent = '';
      return;
    }

    savePill.textContent = `SAVE ${savePercent}%`;
    savePill.hidden = false;
  }

  parseMoney(text) {
    if (!text) return NaN;
    const raw = String(text).replace(/\s/g, '').replace(/[^\d.,-]/g, '');
    if (!raw) return NaN;

    const lastComma = raw.lastIndexOf(',');
    const lastDot = raw.lastIndexOf('.');
    let normalized = raw;
    if (lastComma > -1 || lastDot > -1) {
      const decimalSeparator = lastComma > lastDot ? ',' : '.';
      const thousandSeparator = decimalSeparator === ',' ? '.' : ',';
      normalized = normalized.split(thousandSeparator).join('');
      if (decimalSeparator === ',') normalized = normalized.replace(',', '.');
    }

    const value = Number.parseFloat(normalized);
    return Number.isFinite(value) ? value : NaN;
  }

  ensureMobilePriceStyles() {
    if (document.getElementById(YC_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = YC_STYLE_ID;
    style.textContent = `
      @media screen and (max-width: 749px) {
        .product-details product-price [ref='priceContainer'].${YC_PRICE_ROW_CLASS} {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 7px 8px;
        }

        .product-details product-price [ref='priceContainer'].${YC_PRICE_ROW_CLASS} > [role='group'] {
          display: inline-flex;
          align-items: baseline;
          margin: 0;
        }

        .product-details product-price [ref='priceContainer'].${YC_PRICE_ROW_CLASS} .yc-sale-group {
          order: 1;
        }

        .product-details product-price [ref='priceContainer'].${YC_PRICE_ROW_CLASS} .yc-compare-group {
          order: 2;
        }

        .product-details product-price [ref='priceContainer'].${YC_PRICE_ROW_CLASS} .${YC_SAVE_PILL_CLASS} {
          order: 3;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 3px 10px;
          border-radius: 999px;
          background: #dfe9d7;
          color: #43503d;
          font-size: 13px;
          font-weight: 650;
          line-height: 1.2;
          letter-spacing: 0.02em;
          white-space: nowrap;
        }

        .product-details product-price [ref='priceContainer'].${YC_PRICE_ROW_CLASS} .${YC_SEEDS_CLASS} {
          order: 4;
          display: inline-flex !important;
          margin: 0 !important;
        }

        .product-details product-price [ref='priceContainer'].${YC_PRICE_ROW_CLASS} .${YC_SEEDS_CLASS} .bw-loyalty-earn {
          margin: 0 !important;
          padding: 5px 10px !important;
          border-radius: 999px !important;
          font-size: 0.88rem !important;
          line-height: 1.15 !important;
          gap: 6px !important;
          box-shadow: none !important;
          white-space: nowrap;
        }

        .product-details product-price [ref='priceContainer'].${YC_PRICE_ROW_CLASS} .${YC_SEEDS_CLASS} .bw-loyalty-earn svg {
          width: 16px;
          height: 16px;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
